<template>
  <div class="dlg">
    <header class="dlg__head">
      <div class="dlg__eyebrow">
        <span class="dlg__dot"></span>
        WAIZMANNVERGLEICH / CLEARMENT VERTRAGSSCHUTZ
      </div>
      <h2 class="dlg__title dlg__title--warn">
        Clearment Vertragsschutz
      </h2>
      <p class="dlg__sub">
        Die „Versicherung" für Ihre Zahnzusatzversicherung – damit Sie wirklich
        die Leistung bekommen, die Ihnen vertraglich zusteht.
      </p>
    </header>

    <div class="cs-grid">
      <div class="cs-main">
        <div class="dlg__prose">
          <p>
            Der Clearment Vertragsschutz
            (<a href="https://www.clearment.de" target="_blank" rel="noopener">www.clearment.de</a>)
            <strong>sichert</strong> Ihre vertraglich zugesicherten Leistungen
            ab – damit Ihre Versicherung auch wirklich leistet, wenn Sie sie
            brauchen.
          </p>

          <h3>
            <span class="dlg__prose-eyebrow">1</span>
            Wir lesen für Sie das <span class="text-red">Kleingedruckte</span>
          </h3>
          <p>
            Die Clearment-Anspruchsprüfer arbeiten sich für Sie durch das
            „Kleingedruckte" und ermitteln Ihre berechtigten
            Vertragsansprüche. Anhand des von uns entwickelten
            <a @click="showCheck = !showCheck">Clearment VertragsChecks</a>
            wissen unsere Prüfer genau, welche vertraglichen Ansprüche Ihnen
            zustehen.
          </p>

          <h3>
            <span class="dlg__prose-eyebrow">2</span>
            Wir hängen uns für Sie in die
            <span class="text-red">Warteschleife</span>
          </h3>
          <p>
            Das Clearment-Team telefoniert für Sie mit den
            Leistungsabteilungen der Versicherung und setzt Ihre vertraglichen
            Ansprüche durch. Sie ersparen sich unnötige Zeit und Ärger.
          </p>

          <h3>
            <span class="dlg__prose-eyebrow">3</span>
            Wir belegen Ihre
            <span class="text-red">vertraglichen Ansprüche</span>
          </h3>
          <p>
            Bei strittigen Leistungsfragen können wir Ihre Ansprüche mit Hilfe
            des Clearment VertragsChecks exakt belegen. Im Rahmen des Checks
            lassen wir uns von den Versicherern 157 Fragen zur Leistung und
            zum Vertrag beantworten – im Streitfall wissen wir damit genau,
            wie viel Erstattung Ihnen zusteht.
          </p>
        </div>

        <blockquote class="dlg__quote">
          <em>
            „…das Clearment-Vertragsschutz-Team sah mich nach Sichtung der
            Unterlagen voll im Recht und managte diese für mich sehr
            ärgerliche Angelegenheit umfassend und souverän. Sie berieten
            mich bzgl. einer einzuholenden Stellungnahme und reklamierten
            den Bescheid an höchster Stelle. Schließlich konnten wir einen
            100-prozentigen Erfolg verzeichnen. Die Versicherung hat die
            Kostenübernahme für alle 9 Zähne zugesichert."
          </em>
          <span class="dlg__quote-author">
            Stefan K., Stuttgart · WaizmannTabelle-Kunde
          </span>
        </blockquote>

        <section class="dlg__section">
          <h3 class="dlg__section-title">
            Clearment setzt Ihre Ansprüche durch
          </h3>
          <ul class="cs-cases">
            <li
              v-for="item in pruefungen"
              :key="item.pruef_id"
              class="cs-case"
            >
              <div class="cs-case__amount">
                +{{ item.pruef_ergebnis_value }}&nbsp;€
              </div>
              <div class="cs-case__body">
                <div class="cs-case__case">{{ item.pruef_text }}</div>
                <div class="cs-case__status">
                  <span
                    v-if="item.pruef_ergebnis == 1"
                    class="dlg__pill dlg__pill--ok"
                  >
                    Anspruch durchgesetzt
                  </span>
                  <span
                    v-else-if="item.pruef_ergebnis == 0"
                    class="dlg__pill dlg__pill--info"
                  >
                    in Prüfung
                  </span>
                  <span v-else class="dlg__pill dlg__pill--warn">
                    nicht erstattbar
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </section>
      </div>

      <aside class="cs-side">
        <div class="cs-card">
          <div class="cs-card__icon">
            <img
              :src="require('@/assets/clearmentai/plus.png')"
              alt="Clearment Plus"
            />
          </div>
          <div class="cs-card__title">
            Als Waizmann-Kunde
            <span class="text-pos">kostenlos</span>
          </div>
          <p class="cs-card__text">
            Sie erhalten den Clearment Vertragsschutz während der gesamten
            Vertragslaufzeit ohne Aufpreis.
          </p>
        </div>

        <div class="cs-card cs-card--profile">
          <img
            :src="require('@/assets/clearment/anwalt.png')"
            alt="Florian Ferdinand"
            class="cs-profile__img"
          />
          <div class="cs-profile__name">Florian Ferdinand</div>
          <div class="cs-profile__role">
            Ihr persönlicher Clearment-Anspruchsprüfer seit 2007
          </div>
          <div class="dlg__pill dlg__pill--info cs-profile__stat">
            1.892 erfolgreiche Reklamationen
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
  import { ref, defineProps } from "vue";

  const pruefungen = ref([
    { pruef_id: 1, pruef_text: "Überhöhter Abrechnungsfaktor für eine Wurzelbehandlung", pruef_ergebnis_value: 50, pruef_ergebnis: 1 },
    { pruef_id: 4, pruef_text: "Unberechtigter Abzug des fiktiven GKV-Anteils", pruef_ergebnis_value: 510, pruef_ergebnis: 1 },
    { pruef_id: 45573, pruef_text: "Ablehnung Fissurenversiegelung für Kinder", pruef_ergebnis_value: 90, pruef_ergebnis: 1 },
    { pruef_id: 3, pruef_text: "Ablehnung der Leistung für Digitale Volumentomographie", pruef_ergebnis_value: 260, pruef_ergebnis: 1 },
    { pruef_id: 6, pruef_text: "Analoge Abrechnung einer Sedierung für eine Implantatversorgung", pruef_ergebnis_value: 174, pruef_ergebnis: 1 },
    { pruef_id: 2, pruef_text: "Ablehnung der Kosten für die Verblendung des 7er-Zahns", pruef_ergebnis_value: 110, pruef_ergebnis: 1 },
    { pruef_id: 8, pruef_text: "Abrechnungsfehler bei der Dunkelverarbeitung durch die Versicherung", pruef_ergebnis_value: 230, pruef_ergebnis: 1 },
    { pruef_id: 5, pruef_text: "Überhöhte Verrechnung von Material- und Laborkosten", pruef_ergebnis_value: 670, pruef_ergebnis: 1 },
    { pruef_id: 7, pruef_text: "Leistungsabzug wegen nicht lückenlos gepflegtem Bonusheft", pruef_ergebnis_value: 315, pruef_ergebnis: 1 },
    { pruef_id: 45572, pruef_text: "Ablehnung von Begleitleistungen für Zahnersatz", pruef_ergebnis_value: 63, pruef_ergebnis: 1 },
  ]);

  const showCheck = ref(false);

  defineProps({
    tarif: { type: Object, default: () => ({}) },
    berechnung: { type: Object, default: () => ({}) },
  });
</script>

<style scoped>
  .cs-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 18px;
  }
  @media (min-width: 900px) {
    .cs-grid {
      grid-template-columns: minmax(0, 1fr) 260px;
    }
  }
  .cs-main {
    min-width: 0;
  }
  .cs-side {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .cs-card {
    background: #f7f9fc;
    border: 1px solid #e5e9f0;
    border-radius: 3px;
    padding: 14px;
    text-align: center;
  }
  .cs-card__icon img {
    width: 48px;
    height: auto;
    margin-bottom: 6px;
  }
  .cs-card__title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4px;
  }
  .cs-card__text {
    margin: 0;
    font-size: 12.5px;
    color: #4b5563;
    line-height: 1.5;
  }
  .text-pos {
    color: #2e7d32;
  }
  .text-red {
    color: #b3261e;
  }

  .cs-card--profile {
    text-align: center;
  }
  .cs-profile__img {
    width: 100px;
    height: 100px;
    border-radius: 999px;
    object-fit: cover;
    margin-bottom: 8px;
  }
  .cs-profile__name {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
  }
  .cs-profile__role {
    font-size: 12px;
    color: #4b5563;
    margin: 4px 0 8px;
    line-height: 1.45;
  }
  .cs-profile__stat {
    margin-top: 4px;
  }

  .cs-cases {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .cs-case {
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: 12px;
    align-items: start;
    background: #fff;
    border: 1px solid #e5e9f0;
    border-radius: 3px;
    padding: 8px 12px;
  }
  .cs-case__amount {
    font-size: 16px;
    font-weight: 800;
    color: #2e7d32;
    font-variant-numeric: tabular-nums;
    text-align: right;
    line-height: 1.3;
  }
  .cs-case__case {
    font-size: 13px;
    color: #1f2937;
    line-height: 1.4;
  }
  .cs-case__status {
    margin-top: 4px;
  }
</style>
